/**
 * AcknowledgementPage — public candidate-facing stage acknowledgement page.
 * Unauthenticated: the URL's `token` is the credential (same no-auth,
 * token-in-URL pattern as PublicInterviewPage / PreJoinPage — not wrapped in
 * PublicRoute, no sidebar/Layout). Reached from the acknowledgement mail a
 * recruiter sends via StageAcknowledgementPanel (JobDetailPage's Apply
 * Matching tab).
 *
 * Single-column, centered, phone-first layout — this is opened from an
 * email on a candidate's own device, not an internal dashboard screen.
 */

import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { CheckCircleIcon, XCircleIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import {
  getPublicStageAcknowledgement,
  submitPublicStageAcknowledgement,
  getPublicStageAcknowledgementPdfUrl,
  type PublicStageAcknowledgement,
} from '@/services/publicStageAcknowledgementApi'

type Stage = 'loading' | 'ready' | 'already_done' | 'submitted' | 'error'

export default function AcknowledgementPage() {
  const { token } = useParams<{ token: string }>()
  const [stage, setStage] = useState<Stage>('loading')
  const [data, setData] = useState<PublicStageAcknowledgement | null>(null)
  const [feedback, setFeedback] = useState('')
  const [checked, setChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (!token) {
      setStage('error')
      setErrorMessage('This link is missing its token.')
      return
    }
    let cancelled = false
    getPublicStageAcknowledgement(token)
      .then((res) => {
        if (cancelled) return
        setData(res)
        setFeedback(res.feedback_text || '')
        setStage(res.already_acknowledged ? 'already_done' : 'ready')
      })
      .catch((e: any) => {
        if (cancelled) return
        setErrorMessage(e?.response?.data?.detail || 'This acknowledgement link is invalid or has expired.')
        setStage('error')
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const handleSubmit = async () => {
    if (!token || !checked || submitting) return
    setSubmitting(true)
    try {
      await submitPublicStageAcknowledgement(token, { feedback_text: feedback.trim(), acknowledged: true })
      toast.success('Thank you — your acknowledgement has been recorded.')
      setStage('submitted')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const isSelected = data?.decision === 'Selected'
  const pdfHref = data?.pdf_url || (token ? getPublicStageAcknowledgementPdfUrl(token) : undefined)

  return (
    <div className="min-h-screen bg-gray-50 flex items-start sm:items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg">
        {/* Branding header */}
        <div className="text-center mb-6">
          <span className="text-xl font-bold text-blue-600 tracking-tight">AI Recruit</span>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
          {stage === 'loading' && (
            <div className="py-10 flex justify-center">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {stage === 'error' && (
            <div className="py-6 text-center">
              <XCircleIcon className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600">{errorMessage}</p>
            </div>
          )}

          {(stage === 'already_done' || stage === 'submitted') && data && (
            <div className="py-4 text-center">
              <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h1 className="text-lg font-semibold text-gray-900 mb-1">You've already acknowledged this</h1>
              <p className="text-sm text-gray-500 mb-4">Thank you — no further action is needed.</p>
              {(feedback || data.feedback_text) && (
                <div className="text-left mt-4">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Your submitted comments</label>
                  <div className="w-full px-3 py-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md whitespace-pre-wrap">
                    {feedback || data.feedback_text}
                  </div>
                </div>
              )}
            </div>
          )}

          {stage === 'ready' && data && (
            <div>
              <div className="text-center mb-5">
                <h1 className="text-lg font-semibold text-gray-900">Hi {data.candidate_name},</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Regarding your application for <span className="font-medium text-gray-700">{data.job_title}</span>
                </p>
              </div>

              <div className={`rounded-lg border p-4 mb-5 text-center ${isSelected ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">{data.label}</p>
                {isSelected ? (
                  <p className="text-sm font-medium text-green-800">
                    Congratulations! Your application has been moved forward to the next stage.
                  </p>
                ) : (
                  <p className="text-sm font-medium text-gray-700">
                    Thank you for the time and effort you put into this process. Unfortunately, your application
                    was not selected to move forward this time. We appreciate your interest and encourage you to
                    apply again in the future.
                  </p>
                )}
              </div>

              {pdfHref && (
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 mb-5 bg-white text-blue-600 border border-blue-200 rounded-md text-sm font-medium hover:bg-blue-50"
                >
                  <DocumentArrowDownIcon className="w-4 h-4" />
                  View / Download Decision Letter
                </a>
              )}

              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Any comments or feedback you'd like to share?
                <span className="text-gray-400 font-normal"> (optional)</span>
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="Share any comments or feedback..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none mb-4"
              />

              <label className="flex items-start gap-2 mb-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  I acknowledge that I have received and reviewed this decision.
                </span>
              </label>

              <button
                onClick={handleSubmit}
                disabled={!checked || submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
